import React, { useState, useEffect } from "react"
import Input from "../../../components/ui/Input"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { updateOrganizationProfile, requestOrganizationVerification } from "../../../utils/orgHelpers"
import { readImageAsBase64 } from "../../../utils/imageHelpers"
import { Card } from "../../../components/ui/card"
// Same fields/logic as organization-onboarding, plus name + contact email
// (not editable anywhere else) — both screens read/write the same
// organizations/{orgId} doc via updateOrganizationProfile, so they stay in sync.
const SettingsPanel = ({ organization, onSaved }) => {
  const [formData, setFormData] = useState({
    name: "",
    contactEmail: "",
    logoUrl: "",
    coverImageUrl: "",
    description: "",
    website: "",
    country: "",
    location: "",
    socialLinks: {
      twitter: "",
      linkedin: "",
    },
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [logoError, setLogoError] = useState("")
  const [coverError, setCoverError] = useState("")
  const [showReauth, setShowReauth] = useState(false)
  const [saveMessage, setSaveMessage] = useState("")
  const [isRequestingVerification, setIsRequestingVerification] = useState(false)

  useEffect(() => {
    if (!organization) return
    setFormData({
      name: organization?.name || "",
      contactEmail: organization?.contactEmail || "",
      logoUrl: organization?.logoUrl || "",
      coverImageUrl: organization?.coverImageUrl || "",
      description: organization?.description || "",
      website: organization?.website || "",
      country: organization?.country || "",
      location: organization?.location || "",
      socialLinks: {
        twitter: organization?.socialLinks?.twitter || "",
        linkedin: organization?.socialLinks?.linkedin || "",
      },
    })
  }, [organization])

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e?.target?.value }))
  }

  const handleSocialChange = (platform) => (e) => {
    setFormData((prev) => ({
      ...prev,
      socialLinks: { ...prev?.socialLinks, [platform]: e?.target?.value },
    }))
  }

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return

    setLogoError("")
    const { base64, error } = await readImageAsBase64(file)

    if (error) {
      setLogoError(error?.message)
      return
    }

    setFormData((prev) => ({ ...prev, logoUrl: base64 }))
  }

  const handleCoverUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return

    setCoverError("")
    const { base64, error } = await readImageAsBase64(file)

    if (error) {
      setCoverError(error?.message)
      return
    }

    setFormData((prev) => ({ ...prev, coverImageUrl: base64 }))
  }

  const handleSubmit = (e) => {
    e?.preventDefault()
    setSubmitError("")
    setShowReauth(true)
  }

  const confirmSave = async () => {
    setIsSubmitting(true)

    const { error } = await updateOrganizationProfile(organization?.orgId, formData)

    setIsSubmitting(false)
    setShowReauth(false)

    if (error) {
      setSubmitError(error?.message || "Something went wrong. Please try again.")
      return
    }

    // Lets the nav bar (and anything else) know to re-fetch the org profile,
    // since it only refetches on auth state changes otherwise.
    window.dispatchEvent(new CustomEvent("org-profile-updated", { detail: { orgId: organization?.orgId } }))

    onSaved?.({ ...organization, ...formData })
    setSaveMessage("Saved successfully.")
    setTimeout(() => setSaveMessage(""), 3000)
  }

  const handleRequestVerification = async () => {
    setIsRequestingVerification(true)
    const { error } = await requestOrganizationVerification(organization?.orgId)
    setIsRequestingVerification(false)
    if (!error) {
      onSaved?.({ ...organization, verificationRequested: true })
    }
  }

  return (
    <Card className="p-6 sm:p-8 space-y-4 max-w-2xl md:col-span-4 dark:bg-transparent">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground block">Organization Logo</label>
        {formData?.logoUrl && (
          <img
            src={formData?.logoUrl}
            alt="Logo preview"
            className="w-20 h-20 object-cover rounded-lg border border-border mb-2"
          />
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          className="text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-muted file:text-foreground file:text-sm hover:file:bg-muted/80"
        />
        <p className="text-xs text-muted-foreground">Max 300KB. Square images work best.</p>
        {logoError && <p className="text-sm text-destructive">{logoError}</p>}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground block">Cover Image</label>
        {formData?.coverImageUrl && (
          <img
            src={formData?.coverImageUrl}
            alt="Cover preview"
            className="w-full h-32 object-cover rounded-lg border border-border mb-2"
          />
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleCoverUpload}
          className="text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-muted file:text-foreground file:text-sm hover:file:bg-muted/80"
        />
        <p className="text-xs text-muted-foreground">Max 300KB. Wide images work best.</p>
        {coverError && <p className="text-sm text-destructive">{coverError}</p>}
      </div>

      <Input
        label="Organization Name"
        value={formData?.name}
        onChange={handleChange("name")}
        placeholder="Your organization's name"
      />
      <Input
        label="Contact Email"
        type="email"
        value={formData?.contactEmail}
        onChange={handleChange("contactEmail")}
        placeholder="contact@yourorganization.com"
      />
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">Bio / Description</label>
        <textarea
          rows={4}
          value={formData?.description}
          onChange={handleChange("description")}
          placeholder="What does your organization do?"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      <Input
        label="Website"
        value={formData?.website}
        onChange={handleChange("website")}
        placeholder="https://yourorganization.com"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Country"
          value={formData?.country}
          onChange={handleChange("country")}
          placeholder="e.g. Ghana"
        />
        <Input
          label="Location / City"
          value={formData?.location}
          onChange={handleChange("location")}
          placeholder="e.g. Accra"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Twitter / X"
          value={formData?.socialLinks?.twitter}
          onChange={handleSocialChange("twitter")}
          placeholder="https://x.com/..."
        />
        <Input
          label="LinkedIn"
          value={formData?.socialLinks?.linkedin}
          onChange={handleSocialChange("linkedin")}
          placeholder="https://linkedin.com/..."
        />
      </div>

      {organization?.verified !== undefined && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-xs text-muted-foreground">
            Verification status:{" "}
            {organization?.verified
              ? "Verified"
              : organization?.verificationRequested
              ? "Requested — pending review"
              : "Not verified"}
          </span>
          {!organization?.verified && !organization?.verificationRequested && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRequestVerification}
              loading={isRequestingVerification}
            >
              Request Verification
            </Button>
          )}
        </div>
      )}

      {submitError && <p className="text-sm text-destructive">{submitError}</p>}

      <div className="pt-2 flex items-center gap-3">
        <Button type="button" variant="default" loading={isSubmitting} onClick={handleSubmit}>
          Save Changes
        </Button>
        {saveMessage && <span className="text-sm text-success">{saveMessage}</span>}
      </div>

      <ReauthModal
        open={showReauth}
        label="save changes to your organization profile"
        onConfirm={confirmSave}
        onClose={() => setShowReauth(false)}
      />
    </Card>
  )
}

export default SettingsPanel